import { APP_ROUTES } from './routes'

export const ORDER_STATUS = {
  pending: 'pending',
  confirmed: 'confirmed',
  preparing: 'preparing',
  outForDelivery: 'out_for_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
} as const

export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS]

export const PAYMENT_STATUS = {
  unpaid: 'unpaid',
  paid: 'paid',
  refunded: 'refunded',
} as const

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS]

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  [ORDER_STATUS.pending]: 'Pending',
  [ORDER_STATUS.confirmed]: 'Confirmed',
  [ORDER_STATUS.preparing]: 'Preparing',
  [ORDER_STATUS.outForDelivery]: 'Out for delivery',
  [ORDER_STATUS.delivered]: 'Delivered',
  [ORDER_STATUS.cancelled]: 'Cancelled',
}

export const ORDER_STATUS_FILTERS: { value: 'all' | OrderStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: ORDER_STATUS.pending, label: ORDER_STATUS_LABEL[ORDER_STATUS.pending] },
  { value: ORDER_STATUS.confirmed, label: ORDER_STATUS_LABEL[ORDER_STATUS.confirmed] },
  { value: ORDER_STATUS.preparing, label: ORDER_STATUS_LABEL[ORDER_STATUS.preparing] },
  { value: ORDER_STATUS.outForDelivery, label: ORDER_STATUS_LABEL[ORDER_STATUS.outForDelivery] },
  { value: ORDER_STATUS.delivered, label: ORDER_STATUS_LABEL[ORDER_STATUS.delivered] },
  { value: ORDER_STATUS.cancelled, label: ORDER_STATUS_LABEL[ORDER_STATUS.cancelled] },
]

export const OPEN_ORDER_STATUSES: readonly OrderStatus[] = [
  ORDER_STATUS.pending,
  ORDER_STATUS.confirmed,
  ORDER_STATUS.preparing,
  ORDER_STATUS.outForDelivery,
]

export const ORDER_LIST_LIMIT = 100
export const RECENT_ORDERS_LIMIT = 6

export type OrderAddon = {
  productId: string
  name: string
  unitPrice: number
}

export type OrderItem = {
  id: string
  productId: string | null
  variantId: string | null
  quantity: number
  unitPrice: number
  lineTotal: number
  productName: string
  productSlug: string
  variantLabel: string
  wrapperId: string | null
  wrapperLabel: string | null
  imageUrl: string | null
  addons: OrderAddon[]
}

export type OrderDelivery = {
  recipientName: string
  recipientPhone: string
  address: string
  city: string
  pincode: string | null
  lat: number | null
  lng: number | null
  notes: string | null
}

export type OrderBilling = {
  name: string
  phone: string
  address: string
  city: string
  pincode: string | null
  sameAsShipping: boolean
}

export type Order = {
  id: string
  orderNumber: string
  userId: string
  status: OrderStatus
  paymentStatus: PaymentStatus
  vendorId: string | null
  vendorLocationId: string | null
  isExpress: boolean
  checkoutId: string | null
  delivery: OrderDelivery
  billing: OrderBilling
  items: OrderItem[]
  subtotal: number
  discountTotal: number
  deliveryFee: number
  total: number
  cancelReason: string | null
  cancelledAt: string | null
  createdAt: string
  updatedAt: string
}

export const ORDERS_PAGE = {
  title: 'Orders',
  subtitle: 'Orders assigned to your shop',
  empty: 'No assigned orders yet.',
  emptyFilter: 'No orders match this status.',
  searchPlaceholder: 'Search order number or recipient…',
  detailTitle: 'Order details',
  itemsHeading: 'Items',
  deliveryHeading: 'Shipping',
  slaHeading: 'Delivery promise',
  checkoutHeading: 'Checkout',
  checkoutLinked: 'Linked checkout',
  expressSla: '60 min',
  standardSla: 'Standard',
  billingHeading: 'Billing',
  billingSameAsShipping: 'Same as shipping address',
  loadError: 'Could not load orders.',
  recentTitle: 'Recent orders',
  allLink: 'All orders',
  listPath: APP_ROUTES.orders,
  colOrder: 'Order',
  colRecipient: 'Recipient',
  colCity: 'City',
  colStatus: 'Status',
  colTotal: 'Total',
} as const

export type RecentOrdersProps = {
  orders: Order[]
  loading: boolean
  error: string
}

export type VendorOrderDetailProps = {
  order: Order
  error: string
}
